import { motion } from "motion/react";
import styled, { css } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { greyMui, rydeColors } from "@ryde/theme";

import {
  POLYLINE_TOOL_ICON_REST,
  POLYLINE_TOOL_Z_INDEX,
} from "./polyline-tool-button.constants";

/**
 * One quiet floating card that groups the map tools — mirrors the area-legend
 * panel's surface (white blur card, hairline border, soft shadow) so it reads as
 * part of the product, not a strip of loose colored buttons.
 */
export const PolylineToolbarContainer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "polyline-toolbar",
})`
  position: absolute;
  left: 60px;
  top: 10px;
  display: flex;
  gap: 2px;
  padding: 4px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  border: 1px solid ${greyMui._200};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  /* Above the Leaflet panes/controls this toolbar renders among (kept from the
     original bar); the map container is its stacking context, not the page. */
  z-index: 1000;
`;

export const PolylineToolButtonWrapper = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "polyline-tool-button",
})<{ $active: boolean }>`
  position: relative;
  display: flex;
  /* Lift the hovered button so its tooltip is never covered by a neighbor. */
  z-index: ${({ $active }) =>
    $active ? POLYLINE_TOOL_Z_INDEX.active : POLYLINE_TOOL_Z_INDEX.base};
`;

/**
 * A ghost icon button: neutral at rest, tints to the action's accent on hover,
 * and (only when it's an armed toggle) fills with that accent.
 */
export const PolylineToolButtonStyled = styled(
  motion.button,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "polyline-tool-button-action",
})<{ $color: string; $isActive?: boolean; $isHighlighted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: ${({ $isHighlighted, $color }) =>
    $isHighlighted ? $color : POLYLINE_TOOL_ICON_REST};
  cursor: pointer;
  transition:
    background-color 0.12s ease,
    color 0.12s ease,
    box-shadow 0.12s ease;

  svg {
    font-size: 20px;
  }

  &:hover {
    background-color: ${greyMui._100};
    color: ${({ $color }) => $color};
  }

  &:focus-visible {
    outline: 2px solid ${({ $color }) => $color};
    outline-offset: 1px;
  }

  /* Armed toggle (e.g. delete-point): the one tool that's "on" fills with its
     accent so its active state is unmistakable against the quiet neighbors. */
  ${({ $isActive, $color }) =>
    $isActive &&
    css`
      background-color: ${$color};
      color: ${rydeColors.white};

      &:hover {
        background-color: ${$color};
        color: ${rydeColors.white};
      }
    `}
`;

export const PolylineToolButtonTooltip = styled(
  motion.div,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "polyline-tool-button-tooltip",
})`
  position: absolute;
  top: calc(100% + 8px);
  left: 50% /* @noflip */;
  padding: 5px 9px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: ${rydeColors.white};
  border-radius: 6px;
  background-color: ${greyMui._900};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  pointer-events: none;

  /* Small arrow pointing up at the button. */
  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50% /* @noflip */;
    transform: translateX(-50%) /* @noflip */;
    border: 5px solid transparent;
    border-bottom-color: ${greyMui._900};
  }
`;
